import { apiClient } from "../core/apiClient";
import { IUser } from "../mocks/userData";

export interface ILoginResponse {
  user: IUser;
  accessToken: string;
  refreshToken: string;
  message: string;
}

export interface IRegisterResponse {
  user: IUser;
  accessToken: string;
  refreshToken: string;
  message: string;
}

export interface IRefreshResponse {
  accessToken: string;
  refreshToken: string;
}

export class AuthService {
  private static readonly TOKEN_KEY = "accessToken";
  private static readonly REFRESH_TOKEN_KEY = "refreshToken";
  private static readonly USER_KEY = "user";

  /**
   * Autentica un usuario usando RUT y contraseña
   */
  static async login(rut: string, password: string): Promise<ILoginResponse> {
    try {
      const response = await apiClient.post("/api/v1/auth/login", {
        rut,
        password,
      });

      const { user, accessToken, refreshToken, message } = response.data.data;
      this.saveTokens(accessToken, refreshToken);
      this.saveUser(user);

      return { user, accessToken, refreshToken, message };
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Error de autenticación"
      );
    }
  }

  /**
   * Registra un nuevo usuario (sin login automático)
   */
  static async registerOnly(userData: {
    nombre: string;
    apellido: string;
    email: string;
    password: string;
    rut: string;
  }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post("/api/v1/auth/register", userData);
      return {
        success: true,
        message: response.data.message || "Usuario registrado exitosamente",
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error en el registro");
    }
  }

  /**
   * Registra un nuevo usuario
   */
  static async register(userData: {
    nombre: string;
    apellido: string;
    email: string;
    password: string;
    rut: string;
  }): Promise<IRegisterResponse> {
    try {
      const response = await apiClient.post("/api/v1/auth/register", userData);
      const { user, accessToken, refreshToken, message } = response.data.data;
      this.saveTokens(accessToken, refreshToken);
      this.saveUser(user);
      return { user, accessToken, refreshToken, message };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error en el registro");
    }
  }

  /**
   * Cierra la sesión del usuario
   */
  static async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        await apiClient.post("/api/v1/auth/logout", { refreshToken });
      }
    } catch (error) {
      // No hacer nada, solo limpiar sesión local
    } finally {
      this.clearSession();
    }
  }

  /**
   * Solicita un enlace de recuperación de contraseña
   */
  static async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post("/api/v1/auth/forgot-password", { email });
      return {
        success: true,
        message: response.data.message || "Enlace de recuperación enviado exitosamente",
      };
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Error al enviar el enlace de recuperación"
      );
    }
  }

  /**
   * Restablece la contraseña usando el token de recuperación
   */
  static async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post("/api/v1/auth/reset-password", { 
        token, 
        newPassword 
      });
      return {
        success: true,
        message: response.data.message || "Contraseña restablecida exitosamente",
      };
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Error al restablecer la contraseña"
      );
    }
  }

  /**
   * Refresca el token de acceso usando el refresh token
   */
  static async refreshToken(): Promise<IRefreshResponse> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) throw new Error("No hay refresh token disponible");
      const response = await apiClient.post("/api/v1/auth/refresh", {
        refreshToken,
      });
      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      this.saveTokens(accessToken, newRefreshToken);
      return { accessToken, refreshToken: newRefreshToken };
    } catch (error) {
      this.clearSession();
      throw new Error("Sesión expirada");
    }
  }

  /**
   * Verifica si el token actual es válido llamando a un endpoint protegido
   */
  static async verifyAuthStatus(): Promise<{ isValid: boolean; user?: IUser }> {
    try {
      const token = this.getAccessToken();
      if (!token) return { isValid: false };
      const response = await apiClient.get("/api/v1/users/me");
      return { isValid: true, user: response.data.data };
    } catch (error) {
      return { isValid: false };
    }
  }

  // Utilidades de sesión
  static getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }
  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }
  static getUser(): IUser | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }
  private static saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }
  private static saveUser(user: IUser): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }
  static clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }
}

export default AuthService;
